import React from 'react';
import { Immutable } from 'nuclear-js';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import BatchUtils from '../utils';
import DataUtils from 'data/utils';
import FormattingUtils from 'utils/formatting';
import CommonValues from 'resources/common-values';

export default CreateReactClass({
    displayName: 'batch-print-summary',

    PropTypes: {
        isFamilyReport: PropTypes.bool.isRequired,
        displayPages: PropTypes.instanceOf(Immutable.List),
        pageActive: PropTypes.instanceOf(Immutable.Map).isRequired,
        pagesChanged: PropTypes.bool.isRequired,
        selectedClass: PropTypes.instanceOf(Immutable.Map).isRequired,
        selectedData: PropTypes.instanceOf(Immutable.List).isRequired,
        selectedGroupByOption: PropTypes.oneOf([
            CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC,
            CommonValues.INSTRUCTION_AREAS_GROUPBY_STANDARD,
        ]).isRequired,
        selectedPages: PropTypes.instanceOf(Immutable.Set).isRequired,
        selectedPrintFormat: PropTypes.instanceOf(Immutable.Set).isRequired,
        selectedStudentForPrinting: PropTypes.oneOf(['previous', 'current', 'next']).isRequired,
        selectedStudents: PropTypes.instanceOf(Immutable.Set).isRequired,
        selectedStudentsOption: PropTypes.oneOf(['previous', 'current', 'next']).isRequired,
        selectedSubjects: PropTypes.instanceOf(Immutable.Set).isRequired,
        selectedTermNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        studentInfo: PropTypes.instanceOf(Immutable.Map).isRequired,
        students: PropTypes.instanceOf(Immutable.List).isRequired,
        studentsChanged: PropTypes.bool.isRequired,
        subjectActive: PropTypes.instanceOf(Immutable.Map).isRequired,
        subjectsChanged: PropTypes.bool.isRequired,
        termsWithTestEvents: PropTypes.instanceOf(Immutable.List).isRequired,
        visitedSections: PropTypes.instanceOf(Immutable.Set).isRequired,
    },

    getInitialState() {
        return {
            selectedSubjectsForSummary: Immutable.Set.of(this.props.subjectActive),
            selectedPagesForSummary: Immutable.Set.of(this.props.pageActive),
        };
    },

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.subjectActive !== this.props.subjectActive || nextProps.pageActive !== this.props.pageActive) {
            this.setState({
                selectedSubjectsForSummary: Immutable.Set.of(nextProps.subjectActive),
                selectedPagesForSummary: Immutable.Set.of(nextProps.pageActive),
            });
        }
    },

    getSelectedTermValue(terms, selectedTerm) {
        const term = terms ? terms.find((t) => t.get('termNumber') === selectedTerm) : null;
        return term ? term.get('name') : '';
    },

    getSelectedSubjectValue(selectedSubjects, subjectActive, subjectsChanged) {
        let subjectContent = [];
        if (!this.props.visitedSections.includes('subjects')) {
            return subjectContent;
        } else if (selectedSubjects.size > 0) {
            CommonValues.subjects.forEach((subject) => {
                if (selectedSubjects.includes(subject.get('description'))) {
                    subjectContent.push(<li key={subject.get('description')}>{subject.get('description')}</li>);
                }
            });
        } else if (!subjectsChanged) {
            subjectContent.push(<li key={subjectActive}>{subjectActive}</li>);
        } else if (selectedSubjects.size == 0) {
            subjectContent.push(
                <li className="selection-summary" key="selection-summary">
                    No subject selected
                </li>
            );
        }
        return subjectContent;
    },

    getSelectedDataContent(selectedData) {
        let dataContent = [];
        if (selectedData.size > 0 && this.props.visitedSections.includes('data')) {
            dataContent = selectedData
                .map((data) => {
                    const studyId = data
                        .get('study')
                        .replace(/\s+/g, '-')
                        .toLowerCase();
                    return <li key={`selection-data-${studyId}`}>{data.get('studyDisplayName')}</li>;
                })
                .toArray();
        } else if (selectedData.size == 0 && this.props.visitedSections.includes('data')) {
            dataContent.push(
                <li className="selection-summary" key="selection-data-summary">
                    No projected proficiencies selected
                </li>
            );
        }
        return dataContent;
    },

    getSelectedPageContent(selectedPages, pageActive, pagesChanged, groupBy) {
        let pageContent = [];
        const { visitedSections, isFamilyReport } = this.props;

        if (
            (!visitedSections.includes('students') && isFamilyReport) ||
            (!visitedSections.includes('pages') && !isFamilyReport)
        ) {
            pageContent.push(
                <li className="default-summary-content" key="default-summary-content">
                    A summary of your previously selected options will appear here.
                </li>
            );
        } else if (selectedPages.size > 0 && visitedSections.includes('pages')) {
            this.props.displayPages.forEach((page) => {
                if (selectedPages.includes(page.get('name'))) {
                    if (
                        page.get('name') == BatchUtils.displayPageNames.instructionalAreas &&
                        groupBy == CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC
                    ) {
                        pageContent.push(
                            <li key={page.get('name')}>
                                {page.get('summaryDescription')}
                                {', grouped by topic'}
                            </li>
                        );
                    } else if (
                        page.get('name') == BatchUtils.displayPageNames.instructionalAreas &&
                        groupBy == CommonValues.INSTRUCTION_AREAS_GROUPBY_STANDARD
                    ) {
                        pageContent.push(
                            <li key={page.get('name')}>
                                {page.get('summaryDescription')}
                                {', grouped by standard'}
                            </li>
                        );
                    } else {
                        pageContent.push(<li key={page.get('name')}>{page.get('summaryDescription')}</li>);
                    }
                }
            });
        } else if (!pagesChanged) {
            let pages;
            if (pageActive == 'growth-goals') {
                pages = 'Growth Goals';
            } else if (pageActive == 'instructional-areas' && groupBy == CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC) {
                pages = 'Instructional Areas, grouped by topic';
            } else if (
                pageActive == 'instructional-areas' &&
                groupBy == CommonValues.INSTRUCTION_AREAS_GROUPBY_STANDARD
            ) {
                pages = 'Instructional Areas, grouped by standard';
            } else {
                pages = 'Student Profile';
            }
            pageContent.push(<li key={pageActive}>{pages}</li>);
        } else if (selectedPages.size == 0) {
            pageContent.push(
                <li className="selection-summary" key="selection-summary">
                    No page selected
                </li>
            );
        }
        return pageContent;
    },

    getSelectedStudentContent(option, studentInfo, printFormat, selectedStudents, studentsChanged, students) {
        let studentContent = [];
        let selectedStudentInfo = null;
        if (this.props.selectedTermNumber == CommonValues.mostRecentTermNumber) {
            selectedStudentInfo = DataUtils.latestItemByTerm(studentInfo);
        } else {
            selectedStudentInfo = studentInfo
                ? studentInfo.find((si) => si.get('termNumber', '') == this.props.selectedTermNumber, null, null)
                : null;
        }
        const studentName = selectedStudentInfo ? FormattingUtils.studentFullNameWithInitial(selectedStudentInfo) : '';
        if (option == 'current' && this.props.visitedSections.includes('students') && studentName !== '') {
            studentContent.push(<li key="current-student">{studentName}</li>);
        } else if (this.props.isFamilyReport && !this.props.visitedSections.includes('students')) {
            // Don't return any content (tab hasn't been visited)
        } else if (option == 'all' && this.props.selectedClass) {
            const displayName = this.props.students.size == 1 ? 'student' : 'students';
            studentContent.push(
                <li key="all-student">
                    {this.props.selectedClass.get('name')} -
                    <p
                        className={
                            this.props.students.size > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT
                                ? 'maxStudentCount'
                                : ''
                        }
                    >
                        ({this.props.students.size} {displayName})
                    </p>
                </li>
            );
            if (printFormat.includes('no test'))
                studentContent.push(
                    <li key="no-test" data-testid="student-with-no-test-option">
                        Include students and subjects without tests
                    </li>
                );
            if (printFormat.includes('page break')) studentContent.push(<li key="page-break">2-sided page breaks.</li>);
        } else if (option == 'choose') {
            if (selectedStudents != null && selectedStudents.size == 1) {
                selectedStudents.forEach(function(name) {
                    studentContent.push(<li key={selectedStudents}>{name}</li>);
                });
            } else if (selectedStudents != null && selectedStudents.size > 1 && this.props.selectedClass) {
                studentContent.push(
                    <li key={selectedStudents}>
                        {this.props.selectedClass.get('name')} -
                        <p
                            className={
                                selectedStudents.size > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT
                                    ? 'maxStudentCount'
                                    : ''
                            }
                        >
                            ({selectedStudents.size} {'students'})
                        </p>
                    </li>
                );
            } else if (selectedStudents == null || selectedStudents.size == 0) {
                if (studentsChanged) {
                    studentContent.push(
                        <li
                            className="selection-summary"
                            key="student-selection-summary"
                            data-testid="no-student-selected-summary"
                        >
                            No student selected
                        </li>
                    );
                } else if (students.size == 1) {
                    studentContent.push(<li key={studentName}>{studentName}</li>);
                } else {
                    studentContent.push(
                        <li key={selectedStudents}>
                            {this.props.selectedClass.get('name')} -
                            <p
                                className={
                                    this.props.students.size > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT
                                        ? 'maxStudentCount'
                                        : ''
                                }
                            >
                                ({students.size} {'students'})
                            </p>
                        </li>
                    );
                }
            }
            if (printFormat.includes('no test selected students'))
                studentContent.push(
                    <li key="no-test-selected-students">Include students and subjects without tests</li>
                );
            if (printFormat.includes('page break selected students'))
                studentContent.push(<li key="page-break-selected-students">2-sided page breaks.</li>);
        }
        return studentContent;
    },

    render() {
        const { isFamilyReport } = this.props;
        let selectedTermValue = this.getSelectedTermValue(
            this.props.termsWithTestEvents,
            this.props.selectedTermNumber
        );
        let dataContent = this.getSelectedDataContent(this.props.selectedData);
        let pageContent = this.getSelectedPageContent(
            this.props.selectedPages,
            this.props.pageActive,
            this.props.pagesChanged,
            this.props.selectedGroupByOption
        );
        let subjectContent = this.getSelectedSubjectValue(
            this.props.selectedSubjects,
            this.props.subjectActive,
            this.props.subjectsChanged
        );
        let studentContent = this.getSelectedStudentContent(
            this.props.selectedStudentForPrinting,
            this.props.studentInfo,
            this.props.selectedPrintFormat,
            this.props.selectedStudents,
            this.props.studentsChanged,
            this.props.students
        );

        return (
            <div className="print-batch-summary">
                <h4>SUMMARY</h4>
                <ul>
                    <li>Batch PDF</li>
                    {selectedTermValue && <li>{selectedTermValue}</li>}
                </ul>
                <ul>{subjectContent}</ul>
                <ul>{dataContent}</ul>
                {isFamilyReport ? null : <ul>{pageContent}</ul>}
                <ul>
                    {isFamilyReport ? <li>Family Report</li> : null}
                    {studentContent}
                </ul>
            </div>
        );
    },
});
